import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusIcon, BuildingsIcon, PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";

export default function BranchesPage() {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/branches");
      setItems(r.data);
    } catch { toast.error("Failed to load branches"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8" data-testid="branches-page">
      <div className="flex items-end justify-between border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-brand-orange-ink">Settings</div>
          <h1 className="font-display text-4xl font-black text-brand-blue">Branches</h1>
          <p className="text-slate-500 mt-2">Add your godowns / sub-agencies. Users tagged to a branch only see that branch's data.</p>
        </div>
        <AddDialog onDone={load} />
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-brand-blue-tint border-b border-slate-200">
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Branch</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Address</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Phone</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">GST</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((b) => <BranchRow key={b.id} b={b} onDone={load} />)}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">No branches yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function BranchRow({ b, onDone }) {
  const remove = async () => {
    if (!confirm(`Delete branch "${b.name}"? This is blocked if any user or record is tagged to it.`)) return;
    try {
      await api.delete(`/branches/${b.id}`);
      toast.success("Deleted");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <TableRow className="hover:bg-slate-50 border-b border-slate-100" data-testid={`branch-row-${b.id}`}>
      <TableCell className="font-semibold text-brand-blue flex items-center gap-2">
        <BuildingsIcon size={14} className="text-brand-orange" /> {b.name}
      </TableCell>
      <TableCell className="text-slate-600 text-sm">{b.address || "—"}</TableCell>
      <TableCell className="font-mono-num text-xs">{b.phone || "—"}</TableCell>
      <TableCell className="font-mono-num text-xs">{b.gst || "—"}</TableCell>
      <TableCell className="text-right">
        <button onClick={remove} className="text-slate-400 hover:text-[#FF2400]" data-testid={`branch-delete-${b.id}`}>
          <TrashIcon size={16} />
        </button>
      </TableCell>
    </TableRow>
  );
}

function AddDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [gst, setGst] = useState("");
  const [phone, setPhone] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/branches", { name: name.trim(), address, gst, phone });
      toast.success("Branch added");
      setOpen(false); setName(""); setAddress(""); setGst(""); setPhone("");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white" data-testid="add-branch-button">
          <PlusIcon size={16} className="mr-2" /> Add branch
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300">
        <DialogHeader><DialogTitle className="font-display text-brand-blue">New branch</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-branch-form">
          <div>
            <Label className="overline">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80}
                   placeholder="e.g. Aurangabad Godown"
                   className="rounded-none" data-testid="branch-name-input" />
          </div>
          <div>
            <Label className="overline">Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} className="rounded-none" data-testid="branch-address-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-none" data-testid="branch-phone-input" />
            </div>
            <div>
              <Label className="overline">GSTIN</Label>
              <Input value={gst} onChange={(e) => setGst(e.target.value)} className="rounded-none font-mono-num" data-testid="branch-gst-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-brand-blue hover:bg-brand-blue-ink" data-testid="branch-submit-button">Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
